import { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTrip } from '@/context/TripContext';

export default function RouteResult() {
  const navigate = useNavigate();
  const { currentRoute, spotStatus, checkInSpot, getSpotStatus } = useTrip();

  const [saved, setSaved] = useState(false);
  const [toast, setToast] = useState('');

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2000);
  }, []);

  // 计算默认展开的 Day
  const computeExpandedDays = useCallback(() => {
    if (!currentRoute) return {};
    const init: Record<number, boolean> = {};
    let inProgressDay: number | null = null;
    let firstUnfinishedDay: number | null = null;

    for (const day of currentRoute.daysPlan) {
      const realSpots = day.spots.filter((s) => s.isRealPlace);
      const hasInProgress = realSpots.some(
        (s) => getSpotStatus(s.id, s.status) === 'in-progress',
      );
      const hasUnfinished = realSpots.some(
        (s) => getSpotStatus(s.id, s.status) !== 'completed',
      );

      if (hasInProgress) {
        inProgressDay = day.day;
        break;
      }
      if (firstUnfinishedDay === null && hasUnfinished) {
        firstUnfinishedDay = day.day;
      }
    }

    currentRoute.daysPlan.forEach((day) => {
      if (inProgressDay !== null) {
        init[day.day] = day.day === inProgressDay;
      } else if (firstUnfinishedDay !== null) {
        init[day.day] = day.day === firstUnfinishedDay;
      } else {
        init[day.day] = day.day === currentRoute.days;
      }
    });

    return init;
  }, [currentRoute, getSpotStatus]);

  const [expandedDays, setExpandedDays] = useState<Record<number, boolean>>(computeExpandedDays);

  // 当 spotStatus 或 route 变化时，重新计算展开状态（打卡推进后自动展开下一站所在 Day）
  useEffect(() => {
    setExpandedDays(computeExpandedDays());
  }, [spotStatus, currentRoute, computeExpandedDays]);

  const toggleDay = (day: number) => {
    setExpandedDays((prev) => ({ ...prev, [day]: !prev[day] }));
  };

  const getStatusStyle = (status: string) => {
    switch (status) {
      case 'completed':
        return { icon: 'ri-check-line', color: 'text-bamboo', bg: 'bg-bamboo/10', dot: 'bg-bamboo' };
      case 'in-progress':
        return { icon: 'ri-more-line', color: 'text-tea', bg: 'bg-tea/10', dot: 'bg-tea' };
      case 'skipped':
        return { icon: 'ri-close-line', color: 'text-text-muted', bg: 'bg-gray-100', dot: 'bg-text-muted' };
      default:
        return { icon: '', color: 'text-text-muted', bg: 'bg-gray-100', dot: 'bg-text-muted/30' };
    }
  };

  const handleCheck = (spotId: string) => {
    checkInSpot(spotId);
    showToast('打卡成功');
  };

  const route = currentRoute;

  // 判断是否有已完成点位
  const isAnyCompleted = useMemo(() => {
    if (!route) return false;
    for (const day of route.daysPlan) {
      for (const spot of day.spots) {
        if (!spot.isRealPlace) continue;
        const st = getSpotStatus(spot.id, spot.status);
        if (st === 'completed') return true;
      }
    }
    return false;
  }, [route, getSpotStatus]);

  // 判断行程是否全部完成
  const allCompleted = useMemo(() => {
    if (!route) return false;
    const allReal = route.daysPlan.flatMap((d) => d.spots).filter((s) => s.isRealPlace);
    return allReal.length > 0 && allReal.every((s) => getSpotStatus(s.id, s.status) === 'completed');
  }, [route, getSpotStatus]);

  // 没有 currentRoute 时显示空状态
  if (!route) {
    return (
      <div className="min-h-screen bg-bg pb-20">
        {toast && (
          <div className="fixed top-14 left-0 right-0 z-50 flex justify-center pointer-events-none">
            <div className="bg-text-primary/95 text-white text-sm px-5 py-2 rounded-full shadow-lg backdrop-blur-sm">
              {toast}
            </div>
          </div>
        )}
        <header className="sticky top-0 z-40 bg-bg px-4 pt-3 pb-2">
          <div className="flex items-center gap-2">
            <button
              className="w-8 h-8 flex items-center justify-center cursor-pointer"
              onClick={() => navigate(-1)}
            >
              <i className="ri-arrow-left-line text-lg text-text-body" />
            </button>
            <h1 className="text-lg font-bold text-text-primary">行程详情</h1>
          </div>
        </header>
        <div className="flex flex-col items-center justify-center py-20 text-text-muted">
          <div className="w-20 h-20 rounded-full bg-bamboo-pale flex items-center justify-center mb-4">
            <i className="ri-map-pin-add-line text-bamboo text-3xl" />
          </div>
          <p className="text-base font-medium text-text-primary">还没有生成路线</p>
          <p className="text-sm text-text-muted mt-1">选择天数和偏好，生成你的安吉路线</p>
          <div className="flex gap-3 mt-6">
            <button
              className="px-5 py-2.5 rounded-lg border border-divider text-sm font-medium text-text-body cursor-pointer bg-white"
              onClick={() => navigate('/explore')}
            >
              去找地方
            </button>
            <button
              className="px-5 py-2.5 rounded-lg text-sm font-medium text-white bg-bamboo cursor-pointer"
              onClick={() => navigate('/create-route')}
            >
              去生成路线
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg pb-28">
      {/* Toast */}
      {toast && (
        <div className="fixed top-14 left-0 right-0 z-50 flex justify-center pointer-events-none">
          <div className="bg-text-primary/95 text-white text-sm px-5 py-2 rounded-full shadow-lg backdrop-blur-sm">
            {toast}
          </div>
        </div>
      )}

      {/* 全部完成提示 */}
      {allCompleted && (
        <div className="px-4 mt-3">
          <div className="bg-bamboo-pale rounded-xl p-3 flex items-center gap-2">
            <i className="ri-flag-line text-bamboo text-lg" />
            <p className="text-sm text-bamboo font-medium">行程已全部完成！可以生成旅行记录图分享给朋友</p>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="sticky top-0 z-40 bg-bg px-4 pt-3 pb-2">
        <div className="flex items-center gap-2">
          <button
            className="w-8 h-8 flex items-center justify-center cursor-pointer"
            onClick={() => navigate(-1)}
          >
            <i className="ri-arrow-left-line text-lg text-text-body" />
          </button>
          <h1 className="text-lg font-bold text-text-primary">行程详情</h1>
        </div>
      </header>

      {/* Route Summary */}
      <div className="px-4 mt-2">
        <div className="bg-white rounded-xl p-4">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-bold text-text-primary">{route.name}</h2>
            <span className="text-xs px-2 py-0.5 rounded bg-bamboo-pale text-bamboo">
              {route.crowd}
            </span>
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
            <span className="text-xs text-text-muted flex items-center gap-1">
              <i className="ri-calendar-line" />
              {route.days}天{route.days - 1}晚
            </span>
            <span className="text-xs text-text-muted flex items-center gap-1">
              <i className="ri-walk-line" />
              {route.intensity}
            </span>
            <span className="text-xs text-text-muted flex items-center gap-1">
              <i className="ri-map-pin-line" />
              {route.spotsCount}个地点
            </span>
            <span className="text-xs text-text-muted flex items-center gap-1">
              <i className="ri-car-line" />
              {route.driveTime}
            </span>
          </div>
        </div>
      </div>

      {/* Days */}
      <div className="px-4 mt-4 space-y-3">
        {route.daysPlan.map((day) => {
          const isExpanded = expandedDays[day.day] ?? false;
          const realSpots = day.spots.filter((s) => s.isRealPlace);
          const completedCount = realSpots.filter((s) => getSpotStatus(s.id, s.status) === 'completed').length;
          const totalCount = realSpots.length;
          const hasInProgress = realSpots.some((s) => getSpotStatus(s.id, s.status) === 'in-progress');

          let dayStatusLabel = '未开始';
          let dayStatusClass = 'bg-gray-100 text-text-muted';
          let dayTagText = '';

          if (completedCount === totalCount && totalCount > 0) {
            dayStatusLabel = '已完成';
            dayStatusClass = 'bg-bamboo/10 text-bamboo';
          } else if (hasInProgress) {
            dayStatusLabel = `进行中 ${completedCount}/${totalCount}`;
            dayStatusClass = 'bg-tea/10 text-tea';
            dayTagText = '今日行程';
          } else if (completedCount > 0) {
            dayStatusLabel = `进行中 ${completedCount}/${totalCount}`;
            dayStatusClass = 'bg-tea/10 text-tea';
          }

          return (
            <div key={day.day} className="bg-white rounded-xl overflow-hidden">
              <button
                className="w-full flex items-center justify-between px-4 py-3 cursor-pointer"
                onClick={() => toggleDay(day.day)}
              >
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-bold text-text-primary">{day.title}</h3>
                  {dayTagText && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-bamboo text-white font-medium">
                      {dayTagText}
                    </span>
                  )}
                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${dayStatusClass}`}>
                    {dayStatusLabel}
                  </span>
                </div>
                <i
                  className={`ri-arrow-down-s-line text-text-muted transition-transform ${
                    isExpanded ? 'rotate-180' : ''
                  }`}
                />
              </button>

              {isExpanded && (
                <div className="px-4 pb-4">
                  {/* Today's Advice */}
                  <div className="bg-bamboo-pale/40 rounded-xl p-3 mb-3">
                    <h4 className="text-xs font-bold text-bamboo flex items-center gap-1.5">
                      <i className="ri-lightbulb-line" />
                      今日建议
                    </h4>
                    <div className="mt-2 space-y-1.5">
                      <div className="flex items-start gap-1.5">
                        <i className="ri-time-line text-bamboo-light text-xs mt-0.5" />
                        <p className="text-xs text-text-body">{day.advice.departure}</p>
                      </div>
                      <div className="flex items-start gap-1.5">
                        <i className="ri-car-line text-bamboo-light text-xs mt-0.5" />
                        <p className="text-xs text-text-body">{day.advice.transport}</p>
                      </div>
                      <div className="flex items-start gap-1.5">
                        <i className="ri-home-line text-bamboo-light text-xs mt-0.5" />
                        <p className="text-xs text-text-body">{day.advice.accommodation}</p>
                      </div>
                      <div className="flex items-start gap-1.5">
                        <i className="ri-footprint-line text-bamboo-light text-xs mt-0.5" />
                        <p className="text-xs text-text-body">{day.advice.intensity}</p>
                      </div>
                      {day.advice.notes.map((note, i) => (
                        <div key={i} className="flex items-start gap-1.5">
                          <i className="ri-alert-line text-tea text-xs mt-0.5" />
                          <p className="text-xs text-text-body">{note}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="relative pl-6">
                    {/* Timeline line */}
                    <div className="absolute left-[11px] top-2 bottom-2 w-px bg-divider" />

                    {day.spots.map((spot) => {
                      const status = getSpotStatus(spot.id, spot.status);
                      const statusStyle = getStatusStyle(status);

                      // 建议/转场节点：浅色信息卡，无打卡按钮，不跳转
                      if (!spot.isRealPlace) {
                        return (
                          <div key={spot.id} className="relative mb-3 last:mb-0">
                            <div className="absolute left-[-18px] top-1 w-5 h-5 rounded-full bg-bamboo-pale flex items-center justify-center">
                              <i className="ri-information-line text-bamboo text-[10px]" />
                            </div>
                            <div className="bg-bamboo-pale/30 rounded-xl p-2.5">
                              <div className="flex items-center gap-1.5">
                                <span className="text-xs text-text-muted font-medium">{spot.timeRange}</span>
                                <h4 className="text-sm font-medium text-text-primary">{spot.name}</h4>
                              </div>
                              <p className="text-xs text-text-muted mt-0.5">{spot.tips}</p>
                            </div>
                          </div>
                        );
                      }

                      return (
                        <div key={spot.id} className="relative mb-4 last:mb-0">
                          {/* Timeline dot */}
                          <div
                            className={`absolute left-[-18px] top-1 w-5 h-5 rounded-full flex items-center justify-center ${statusStyle.bg}`}
                          >
                            {status === 'completed' ? (
                              <i className={`${statusStyle.icon} text-xs ${statusStyle.color}`} />
                            ) : status === 'in-progress' ? (
                              <div className="w-2 h-2 rounded-full bg-tea" />
                            ) : (
                              <div className="w-2 h-2 rounded-full bg-text-muted/30" />
                            )}
                          </div>

                          <div className="bg-white rounded-xl border border-divider/60 p-3">
                            {/* 头部：时间 + 状态标签，可点击跳转详情 */}
                            <div
                              className={`flex items-start justify-between gap-2 ${spot.placeId ? 'cursor-pointer' : ''}`}
                              onClick={() => {
                                if (spot.placeId) navigate(`/place/${spot.placeId}`);
                              }}
                            >
                              <div>
                                <span className="text-xs text-bamboo font-medium">{spot.timeRange}</span>
                                <h4 className="text-sm font-bold text-text-primary mt-0.5">{spot.name}</h4>
                                <p className="text-xs text-text-muted mt-0.5">{spot.duration}</p>
                              </div>
                              {status === 'completed' && (
                                <span className="flex items-center gap-1 text-xs text-bamboo font-medium flex-shrink-0 bg-bamboo/10 px-2 py-1 rounded-lg">
                                  <i className="ri-check-double-line" />
                                  已游玩
                                </span>
                              )}
                              {status === 'in-progress' && (
                                <span className="flex items-center gap-1 text-xs text-tea font-medium flex-shrink-0 bg-tea/10 px-2 py-1 rounded-lg">
                                  <i className="ri-play-circle-line" />
                                  进行中
                                </span>
                              )}
                            </div>

                            {/* 固定提醒 */}
                            {spot.tips && (
                              <p className="text-[10px] text-text-muted mt-2 bg-bamboo-pale/30 rounded-lg px-2.5 py-1.5 leading-relaxed">
                                {spot.tips}
                              </p>
                            )}

                            {/* 拍照点 */}
                            {spot.photoSpots && spot.photoSpots.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {spot.photoSpots.map((ps) => (
                                  <span
                                    key={ps}
                                    className="text-[10px] px-1.5 py-0.5 rounded bg-stream/10 text-stream"
                                  >
                                    <i className="ri-camera-line mr-0.5" />
                                    {ps}
                                  </span>
                                ))}
                              </div>
                            )}

                            {/* 特色菜 */}
                            {spot.specialties && spot.specialties.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {spot.specialties.map((sp) => (
                                  <span
                                    key={sp}
                                    className="text-[10px] px-1.5 py-0.5 rounded bg-bamboo-pale text-bamboo"
                                  >
                                    <i className="ri-restaurant-line mr-0.5" />
                                    {sp}
                                  </span>
                                ))}
                              </div>
                            )}

                            {/* 下一站车程 */}
                            {spot.nextDrive && (
                              <div className="flex items-center gap-1 mt-2 text-[10px] text-text-muted">
                                <i className="ri-car-line" />
                                {spot.nextDrive}
                                {spot.nextDriveDuration && (
                                  <span className="text-stream">（{spot.nextDriveDuration}）</span>
                                )}
                              </div>
                            )}

                            {/* 底部操作区 */}
                            <div className="flex items-center gap-2 mt-3 pt-2 border-t border-divider/50">
                              {status !== 'completed' && (
                                <button
                                  className={`px-3 py-1.5 rounded-lg text-white text-xs font-medium cursor-pointer whitespace-nowrap ${
                                    status === 'in-progress' ? 'bg-tea' : 'bg-bamboo'
                                  }`}
                                  onClick={() => handleCheck(spot.id)}
                                >
                                  <i className="ri-checkbox-circle-line mr-0.5" />
                                  打卡完成
                                </button>
                              )}
                              {status === 'completed' && (
                                <span className="text-xs text-bamboo font-medium px-2 py-1 rounded bg-bamboo/10">
                                  <i className="ri-check-double-line mr-0.5" />
                                  已打卡
                                </span>
                              )}

                              <button
                                className="flex items-center gap-1 text-[10px] text-text-muted cursor-pointer px-2 py-1"
                                onClick={() => showToast('照片上传功能开发中')}
                              >
                                <i className="ri-camera-line" />
                                上传照片
                              </button>
                              <button
                                className="flex items-center gap-1 text-[10px] text-text-muted cursor-pointer px-2 py-1"
                                onClick={() => showToast('备注功能开发中')}
                              >
                                <i className="ri-edit-line" />
                                写备注
                              </button>
                              <button
                                className="flex items-center gap-1 text-[10px] text-stream cursor-pointer ml-auto px-2 py-1"
                                onClick={() => showToast(`导航至${spot.name}`)}
                              >
                                <i className="ri-navigation-line" />
                                导航
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Bottom Action Bar */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-divider px-4 py-3">
        <div className="flex items-center gap-2 max-w-lg mx-auto">
          <button
            className="flex-1 py-2.5 rounded-lg border border-divider text-sm font-medium text-text-body cursor-pointer bg-white"
            onClick={() => {
              showToast('行程已保存');
              setTimeout(() => navigate('/my-trips'), 400);
            }}
          >
            <i className="ri-eye-line mr-1" />
            查看行程
          </button>
          <button
            className="flex-1 py-2.5 rounded-lg border border-divider text-sm font-medium text-text-body cursor-pointer bg-white"
            onClick={() => navigate('/create-route')}
          >
            <i className="ri-edit-line mr-1" />
            调整路线
          </button>
          <button
            className="flex-1 py-2.5 rounded-lg text-sm font-medium text-white bg-bamboo cursor-pointer"
            onClick={() => navigate('/share')}
          >
            <i className={`${allCompleted ? 'ri-flag-line' : 'ri-share-line'} mr-1`} />
            {allCompleted ? '结束行程' : '分享行程'}
          </button>
        </div>
      </div>
    </div>
  );
}
